import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { Modal } from '../components/Modal';
import { Confirm } from '../components/Confirm';
import { DataTable } from '../components/DataTable';
import { Badge } from '../components/Badge';
import { BotonEstadoPublicacion, BotonReenviarStock } from '../components/AccionesPublicacionMl';
import { Button } from '../components/Button';
import { StockSku, type ResumenStockSku } from '../components/StockSku';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { etiquetaArticulo, textoArticulo, useFilasConIdentidad, useIdentidades } from '../lib/identidadArticulos';

/**
 * Vista previa de la sincronización de stock con Mercado Libre de un SKU («Ver» en Sincronizar con ML; hasta 2026-10-20
 * era la solapa Sincronización por SKU, que se sacó). Qué se haría en cada publicación (el preview no envía nada),
 * "Empezar a sincronizar" (con sus packs), "Sincronizar también sus packs", "Detener", el stock de resguardo y "Contar
 * este SKU".
 * La escritura general en Mercado Libre sigue deshabilitada: sólo los SKU que sincronizan envían su stock (y pausan en 0 /
 * reactivan lo que pausó el sistema), y sólo en las publicaciones cuyos SKU (packs y combos incluidos) sincronizan todos.
 * Para el usuario hay un solo verbo: sincronizar / dejar de sincronizar (la API sigue diciendo habilitar/detener).
 * Cada SKU simple guarda una reserva de estante (2 por defecto, la cambia un admin; 0 = sin reserva) que no se publica
 * hasta que alguien cuenta el SKU y confirma el conteo. Se cuenta en un solo lugar, Conteos de inventario (DECISIONES
 * 2026-10-02): "Contar este SKU" lleva ahí con el SKU.
 * Publicaciones Full (2026-09-30): con "Escribir el stock de depósito en publicaciones Full" (sólo admin, apagado por
 * defecto) se escribe sólo su stock de depósito propio, nunca el de Full ni el estado. La vista previa lo muestra siempre.
 */

/** `deposito`: publicación Full, se escribe sólo el stock de depósito propio de su user product. */
type AccionSyncSku = 'enviar' | 'pausar' | 'reactivar' | 'sin_cambios' | 'omitir' | 'deposito';

interface FilaPreview {
  mlaId: string;
  variantId: string | null;
  titulo: string | null;
  skuPublicacion: string;
  esCombo: boolean;
  esVariante: boolean;
  userProductId: string | null;
  esCatalogo: boolean;
  unidadesPorPublicacion: number | null;
  stockCalculado: number | null;
  stockMl: number | null;
  statusMl: string | null;
  fuenteMl: 'ml' | 'local';
  skusSinHabilitar: string[];
  /** Su fila de 02_CODIGOS dice que el SKU se compone de sí mismo × N: no se escribe hasta corregir la planilla. */
  autorreferencia?: number | null;
  accion: AccionSyncSku;
  cantidadAEnviar: number | null;
  motivo: string | null;
  /** Publicación Full según Mercado Libre: nunca se pausa ni se reactiva. */
  esFull?: boolean;
  /** Stock de depósito propio del user product en Mercado Libre (null si no tiene o no se leyó). */
  stockDeposito?: number | null;
  /** Stock en Full del user product (nunca se escribe). */
  stockEnFull?: number | null;
  /** Variante cuyo stock publica la variación (switch prendido); null o ausente = el total. */
  variante?: { id: number; nombre: string; colorHex: string | null; stockErp: number; limita?: boolean } | null;
}

interface PreviewSyncSku {
  sku: string;
  descripcion: string | null;
  habilitado: boolean;
  activadoEn: string | null;
  activadoPor: string | null;
  ultimaSyncEn: string | null;
  ultimaSyncOk: boolean | null;
  ultimaSyncResultado: string | null;
  fisico: number;
  reservado: number;
  disponible: number;
  esCombo: boolean;
  /** Stock del SKU (packs/combos en unidades del pack); los tres números de arriba son los del SKU tal cual. */
  stock?: ResumenStockSku | null;
  reserva: number;
  reservaLiberada: boolean;
  reservaLiberadaEn: string | null;
  reservaLiberadaPor: string | null;
  reservaRegeneradaEn: string | null;
  reservaVigente: number;
  vendible: number | null;
  pendienteVerificacion: boolean;
  /** Packs del SKU (una sola hoja en su BOM: este SKU): empiezan y dejan de sincronizar con él. */
  packs: PackDeSku[];
  packsSinHabilitar: number;
  /** Calculadas como si el SKU y sus packs ya estuvieran habilitados. */
  publicaciones: FilaPreview[];
  /** "Escribir el stock de depósito en publicaciones Full": apagado, lo planificado para las Full no se escribe. */
  escrituraDepositoFull?: boolean;
  /** El SKU (y las hojas de sus packs) tiene conteo; false: se avisa antes de sincronizar (no bloquea). */
  contado?: boolean;
  /** null o ausente: el SKU no tiene variantes. `aviso` lo arma el backend. */
  variantes?: { stockPorVarianteEnMl: boolean; sinAsignar: number; aviso: string | null } | null;
}

interface PackDeSku {
  sku: string;
  descripcion: string | null;
  unidadesPorPack: number;
  habilitado: boolean;
  publicacionesActivas: number;
}

interface ResultadoHabilitar {
  packs?: string[];
  resultado: { enviados: number; depositoActualizados?: number; fallidos: number; sinCambios: number; omitidas: number };
}

/** Qué confirma el modal: empezar con la base (y sus packs) o, con la base sincronizando, sincronizar sólo sus packs. */
type Confirmacion = 'inicio' | 'packs';

const URL_BASE = '/api/v3/ml/sync-sku';

const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('es-AR'));
const fecha = (v: string | null) => (v ? new Date(v).toLocaleString('es-AR') : '—');

const ACCION: Record<AccionSyncSku, { texto: (f: FilaPreview) => string; tone: 'blue' | 'amber' | 'green' | 'slate' | 'red' }> = {
  enviar: { texto: (f) => `Enviar ${n(f.cantidadAEnviar)}`, tone: 'blue' },
  pausar: { texto: () => 'Pausar (sin stock)', tone: 'amber' },
  reactivar: { texto: (f) => `Reactivar con ${n(f.cantidadAEnviar)}`, tone: 'green' },
  sin_cambios: { texto: () => 'Sin cambios', tone: 'slate' },
  omitir: { texto: () => 'No se escribe', tone: 'red' },
  deposito: { texto: (f) => `Stock de depósito: ${n(f.cantidadAEnviar)}`, tone: 'blue' },
};

export const SWITCH_DEPOSITO_FULL = 'Escribir el stock de depósito en publicaciones Full';
/** Confirmación de encender el switch (la usa «Sincronizar con ML»). */
export const MENSAJE_ENCENDER_DEPOSITO_FULL =
  'Desde la próxima sincronización (a más tardar en 5 minutos, con el control periódico) el ERP va a escribir en Mercado Libre el '
  + 'stock de depósito propio de las publicaciones Full de los SKU que sincronizan. El stock de Full no se toca y esas publicaciones '
  + 'no se pausan ni se reactivan. Conviene revisar antes la vista previa de esos SKU.';
/** Aviso (no bloquea) del preview de un SKU sin conteo: su stock no está declarado. */
const AVISO_SIN_CONTAR = 'Este SKU todavía no tiene un conteo: lo que se publique no sale de un stock declarado. Contalo antes de sincronizar.';

const seEscribe = (f: FilaPreview) => f.accion === 'enviar' || f.accion === 'pausar' || f.accion === 'reactivar';
const nombrePublicacion = (f: FilaPreview) => `${f.mlaId}${f.variantId ? ` / variante ${f.variantId}` : ''}`;
const nombrePack = (p: PackDeSku) => `${p.sku} (x${p.unidadesPorPack.toLocaleString('es-AR')})`;
const conPacks = (packs: string[] | undefined) => (packs && packs.length > 0 ? ` y sus packs ${packs.join(', ')}` : '');
const cuantosPacks = (packs: string[]) => `${packs.length} ${packs.length === 1 ? 'pack' : 'packs'}: ${packs.join(', ')}`;

/** `onCambio`: después de empezar, detener o cambiar el resguardo (la pantalla vuelve a cargar su lista). */
export function VistaPreviaSyncSku({ sku, onCambio }: { sku: string; onCambio?: () => void }) {
  const { user, can } = useAuth();
  const { addToast } = useToast();
  const esAdmin = user?.role === 'admin';
  // Mismos permisos que el backend: empezar sólo admin; detener también con catalogo:editar.
  const puedeDetener = esAdmin || can('catalogo', 'editar');
  // "Contar este SKU": crear un conteo de inventario pide wms:editar.
  const puedeContar = esAdmin || can('wms', 'editar');

  const [preview, setPreview] = useState<PreviewSyncSku | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState<Confirmacion | null>(null);
  const [aDetener, setADetener] = useState<{ sku: string; packs: string[] } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [reservaTexto, setReservaTexto] = useState('');
  // Titles, labels and toasts name articles by code (· supplier when repeated), never by the internal ID.
  const identidad = useIdentidades([preview?.sku, ...(preview?.packs ?? []).map((p) => p.sku), ...(aDetener?.packs ?? [])]);
  const etiqueta = (s: string) => etiquetaArticulo(s, identidad(s));

  const cargarPreview = useCallback(async (s: string) => {
    setCargando(true);
    setError(null);
    try {
      const p = await api.get<PreviewSyncSku>(`${URL_BASE}/preview?sku=${encodeURIComponent(s)}`);
      setPreview(p);
      setReservaTexto(String(p.reserva ?? ''));
    } catch (e) {
      setPreview(null);
      setError(extractErrorMessage(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void cargarPreview(sku); }, [cargarPreview, sku]);

  /**
   * "Confirmar y empezar" (base y sus packs) o "Confirmar y sincronizar" (sólo sus packs sin sincronizar). Manda los packs
   * que mostró el modal: si el servidor calcula otros (409), no sincroniza nada y se vuelve a cargar la vista previa.
   */
  const confirmarSincronizacion = async () => {
    if (!preview || !confirmar) return;
    const soloPacks = confirmar === 'packs';
    setGuardando(true);
    try {
      const r = await api.post<ResultadoHabilitar>(`${URL_BASE}/${soloPacks ? 'habilitar-packs' : 'habilitar'}`, {
        sku: preview.sku, packs: packsASincronizar.map((p) => p.sku),
      });
      const { enviados, fallidos } = r.resultado;
      const depositos = r.resultado.depositoActualizados ?? 0;
      const packs = r.packs ?? [];
      const que = soloPacks
        ? `Sincronización de los packs de ${etiqueta(preview.sku)} iniciada (${packs.length > 0 ? cuantosPacks(packs.map(etiqueta)) : 'ningún pack pendiente'})`
        : `Sincronización de ${etiqueta(preview.sku)} iniciada${packs.length > 0 ? ` (${cuantosPacks(packs.map(etiqueta))})` : ''}`;
      addToast(
        `${que}: ${n(enviados)} publicaciones enviadas${depositos > 0 ? `, ${n(depositos)} stock de depósito actualizado` : ''}`
          + `${fallidos > 0 ? `, ${n(fallidos)} con error (se reintenta sola)` : ''}`,
        fallidos > 0 ? 'error' : 'success',
      );
      setConfirmar(null);
      onCambio?.();
      await cargarPreview(preview.sku);
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
      if ((e as { status?: number } | null)?.status === 409) {
        setConfirmar(null);
        await cargarPreview(preview.sku);
      }
    } finally {
      setGuardando(false);
    }
  };

  const detener = async () => {
    if (!aDetener) return;
    const { sku: aParar } = aDetener;
    setGuardando(true);
    try {
      const r = await api.post<{ packs?: string[] }>(`${URL_BASE}/detener`, { sku: aParar });
      addToast(`Se dejó de sincronizar ${etiqueta(aParar)}${conPacks(r?.packs?.map(etiqueta))}`, 'success');
      setADetener(null);
      onCambio?.();
      await cargarPreview(aParar);
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const guardarReserva = async () => {
    if (!preview) return;
    const reserva = Number(reservaTexto);
    if (reservaTexto.trim() === '' || !Number.isInteger(reserva) || reserva < 0 || reserva > 1000) {
      addToast('El stock de resguardo tiene que ser un número entero entre 0 y 1000', 'error');
      return;
    }
    setGuardando(true);
    try {
      await api.post(`${URL_BASE}/reserva`, { sku: preview.sku, reserva });
      addToast(`Stock de resguardo de ${etiqueta(preview.sku)}: ${n(reserva)}${reserva === 0 ? ' (sin stock de resguardo)' : ''}`, 'success');
      onCambio?.();
      await cargarPreview(preview.sku);
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Publications with their article's code and identity supplier (cells and the CSV export).
  const filasPublicaciones = useFilasConIdentidad(preview?.publicaciones ?? [], (f) => f.skuPublicacion);

  if (error) return <p role="alert" className="text-sm text-red-600">{error}</p>;
  if (!preview) return <p className="text-sm text-slate-500">Cargando…</p>;

  const aEnviar = preview.publicaciones.filter(seEscribe);
  const omitidas = preview.publicaciones.filter((f) => f.accion === 'omitir');
  // Publicaciones Full con stock de depósito a actualizar: sólo se escriben con el switch encendido.
  const depositosFull = preview.publicaciones.filter((f) => f.accion === 'deposito');
  const depositoEncendido = preview.escrituraDepositoFull === true;
  const accionDe = (f: FilaPreview) => {
    if (f.autorreferencia) return { texto: 'Revisar', tone: 'amber' as const };
    return f.accion === 'deposito' && !depositoEncendido
      ? { texto: `${ACCION.deposito.texto(f)} (apagado)`, tone: 'amber' as const }
      : { texto: ACCION[f.accion].texto(f), tone: ACCION[f.accion].tone };
  };
  const packsDelPreview = preview.packs ?? [];
  // Los que sincroniza el botón: los packs sin sincronizar (uno que ya sincroniza conserva quién y cuándo lo inició).
  const packsASincronizar = packsDelPreview.filter((p) => !p.habilitado);
  const packsSincronizando = packsDelPreview.filter((p) => p.habilitado).map((p) => p.sku);

  return (
    <>
      <section aria-label={`Vista previa de ${etiqueta(preview.sku)}`} className="space-y-3">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-800"><CodigoArticulo sku={preview.sku} /></h3>
              <Badge tone={preview.habilitado ? 'green' : 'slate'}>{preview.habilitado ? 'Sincronizando' : 'Sin sincronizar'}</Badge>
            </div>
            {preview.descripcion && <p className="text-sm text-slate-600">{preview.descripcion}</p>}
            <StockSku
              className="mt-1"
              stock={preview.stock ?? {
                fisico: preview.fisico, reservado: preview.reservado, disponible: preview.disponible,
                esCombo: preview.esCombo, limitante: null, motivo: null,
              }}
              despues={(
                <>
                  {!preview.esCombo && (
                    <> · Stock de resguardo: <strong>{n(preview.reserva)}{preview.reservaLiberada ? ' (liberado)' : ''}</strong></>
                  )}
                  {' · '}Se publica: <strong>{n(preview.vendible)}</strong>
                </>
              )}
            />
            {preview.esCombo ? (
              <p className="text-xs text-slate-500 mt-1">Pack/combo: hereda el stock de resguardo de sus componentes.</p>
            ) : (
              <p className="text-xs text-slate-500 mt-1 max-w-xl">
                El stock de resguardo queda en el estante y no se publica hasta que alguien cuenta el SKU y confirma el conteo; ahí
                se libera y vuelve con la próxima carga del proveedor. 0 = sin stock de resguardo. Lo cambia un administrador.
              </p>
            )}
            {preview.contado === false && (
              <p role="note" className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                {AVISO_SIN_CONTAR}
              </p>
            )}
            {preview.pendienteVerificacion && (
              <p className="mt-1"><Badge tone="amber">Pausado: solo queda el stock de resguardo (contar en Conteos de inventario)</Badge></p>
            )}
            {preview.reservaLiberada && preview.reservaLiberadaEn && (
              <p className="text-xs text-slate-500 mt-1">Stock de resguardo liberado por {preview.reservaLiberadaPor ?? '—'} el {fecha(preview.reservaLiberadaEn)}; vuelve con la próxima carga del proveedor.</p>
            )}
            {preview.habilitado && (
              <p className="text-xs text-slate-500 mt-1">
                Sincronización iniciada por {preview.activadoPor ?? '—'} el {fecha(preview.activadoEn)} · Última sincronización: {fecha(preview.ultimaSyncEn)}
                {preview.ultimaSyncResultado ? ` (${preview.ultimaSyncResultado})` : ''}
              </p>
            )}
            {packsDelPreview.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-slate-600">Sus packs (empiezan y dejan de sincronizar con este SKU):</p>
                <ul aria-label={`Packs de ${etiqueta(preview.sku)}`} className="text-xs text-slate-600 space-y-0.5 mt-0.5">
                  {packsDelPreview.map((p) => (
                    <li key={p.sku}>
                      <strong><CodigoArticulo sku={p.sku} /></strong> (x{p.unidadesPorPack.toLocaleString('es-AR')}){p.descripcion ? ` · ${p.descripcion}` : ''}
                      {' · '}{n(p.publicacionesActivas)} publicaciones{' · '}
                      <Badge tone={p.habilitado ? 'green' : 'slate'}>{p.habilitado ? 'Sincronizando' : 'Sin sincronizar'}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.habilitado && preview.packsSinHabilitar > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                <span>
                  {`${n(preview.packsSinHabilitar)} ${preview.packsSinHabilitar === 1 ? 'pack sin sincronizar' : 'packs sin sincronizar'}: sus publicaciones no se escriben hasta sincronizarlos.`}
                </span>
                {esAdmin && (
                  <Button size="sm" variant="secondary" onClick={() => setConfirmar('packs')} disabled={cargando || guardando}>Sincronizar también sus packs</Button>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-start">
            {!preview.esCombo && esAdmin && (
              <div className="flex items-end gap-1">
                <input
                  type="number"
                  min={0}
                  max={1000}
                  step={1}
                  aria-label="Stock de resguardo"
                  className="w-20 rounded border border-slate-300 px-2 py-1 text-sm"
                  value={reservaTexto}
                  onChange={(e) => setReservaTexto(e.target.value)}
                />
                <Button variant="secondary" size="sm" onClick={() => void guardarReserva()} disabled={guardando}>Guardar stock de resguardo</Button>
              </div>
            )}
            {!preview.esCombo && puedeContar && (
              <Link
                to={`/conteos-inventario?sku=${encodeURIComponent(preview.sku)}`}
                className="inline-flex items-center justify-center rounded-lg font-medium px-4 py-2 text-sm min-h-[2.5rem] bg-slate-600 hover:bg-slate-700 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1"
              >
                Contar este SKU
              </Link>
            )}
            {!preview.habilitado && esAdmin && (
              <Button onClick={() => setConfirmar('inicio')} disabled={cargando || guardando}>Empezar a sincronizar</Button>
            )}
            {!preview.habilitado && !esAdmin && (
              <span className="text-xs text-slate-400">Sólo un administrador puede empezar a sincronizar un SKU.</span>
            )}
            {preview.habilitado && <BotonReenviarStock sku={preview.sku} onHecho={() => void cargarPreview(preview.sku)} />}
            {preview.habilitado && puedeDetener && (
              <Button variant="danger" onClick={() => setADetener({ sku: preview.sku, packs: packsSincronizando })} disabled={guardando}>Dejar de sincronizar</Button>
            )}
          </div>
        </div>

        <DataTable<FilaPreview>
          title={`Sincronizacion-${textoArticulo(preview.sku, identidad(preview.sku))}`}
          enableXlsxExport={false}
          data={filasPublicaciones}
          emptyMessage="El SKU no tiene publicaciones vinculadas (ni packs o combos que lo contengan)"
          columns={[
            {
              key: 'mlaId', header: 'Publicación',
              render: (f) => (
                <div>
                  <div className="font-medium">{nombrePublicacion(f)}</div>
                  <div className="text-xs text-slate-500">{f.titulo ?? '—'}</div>
                  {f.variante && (
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-600">
                      <span aria-hidden className="inline-block h-3 w-3 rounded-sm border border-slate-300" style={{ backgroundColor: f.variante.colorHex ?? 'transparent' }} />
                      <span>{f.variante.limita ? `Limita: ${f.variante.nombre}` : f.variante.nombre}</span>
                      <span>{`· Stock ERP ${n(f.variante.stockErp)}`}</span>
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'codigoArticulo', header: 'SKU de la publicación',
              render: (f) => <span><CodigoArticulo sku={f.skuPublicacion} />{f.esCombo && <span className="text-xs text-slate-500"> (pack/combo)</span>}</span>,
            },
            { key: 'proveedorArticulo', header: 'Proveedor' },
            { key: 'unidadesPorPublicacion', header: 'Unidades por publicación', align: 'right', render: (f) => n(f.unidadesPorPublicacion) },
            { key: 'stockCalculado', header: 'Stock calculado', align: 'right', render: (f) => n(f.stockCalculado) },
            {
              key: 'stockMl', header: 'En Mercado Libre',
              render: (f) => (
                <span>
                  {n(f.stockMl)} · {f.statusMl ?? '—'}
                  {/* Las variantes comparten el estado: un solo botón por publicación. */}
                  {filasPublicaciones.find((x) => x.mlaId === f.mlaId) === f && (
                    <span className="ml-2"><BotonEstadoPublicacion mlaId={f.mlaId} status={f.statusMl} onCambio={() => void cargarPreview(sku)} /></span>
                  )}
                  {f.fuenteMl === 'local' && <span className="text-xs text-amber-700" title="No se pudo leer Mercado Libre: es el valor de la última vez que se trajeron las publicaciones"> (sin leer ML)</span>}
                  {f.esFull && (
                    <span className="block text-xs text-slate-500" title="Stock del producto de Mercado Libre: depósito propio y Full (el de Full no se toca)">
                      Depósito {n(f.stockDeposito)} · Full {n(f.stockEnFull)}
                    </span>
                  )}
                </span>
              ),
            },
            {
              key: 'accion', header: 'Qué se haría',
              render: (f) => <Badge tone={accionDe(f).tone}>{accionDe(f).texto}</Badge>,
            },
            { key: 'motivo', header: 'Detalle', render: (f) => <span className="text-xs text-slate-600">{f.motivo ?? ''}</span> },
          ]}
        />
        {preview.variantes && (
          <div className="text-xs text-slate-600">
            <p><span>Sin asignar</span>: <strong className="text-slate-800">{n(preview.variantes.sinAsignar)}</strong></p>
            {preview.variantes.aviso && (
              <p role="note" className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">{preview.variantes.aviso}</p>
            )}
          </div>
        )}
      </section>

      <Modal
        open={confirmar !== null}
        onClose={() => setConfirmar(null)}
        title={confirmar === 'packs' ? `Sincronizar los packs de ${etiqueta(preview.sku)}` : `Empezar a sincronizar ${etiqueta(preview.sku)}`}
        size="md"
      >
        <div className="space-y-3 text-sm text-slate-700">
          {confirmar === 'packs' ? (
            <p>
              Empiezan a sincronizar: <strong>{packsASincronizar.map(nombrePack).join(', ')}</strong>. Desde ahora el ERP también va a escribir en
              Mercado Libre el stock de sus publicaciones. Ahora mismo se enviaría:
            </p>
          ) : (
            <>
              {packsASincronizar.length > 0 && (
                <p className="rounded border border-slate-200 bg-slate-50 px-2 py-1">
                  También empiezan a sincronizar sus packs: <strong>{packsASincronizar.map(nombrePack).join(', ')}</strong>.
                </p>
              )}
              <p>
                Desde ahora el ERP va a escribir en Mercado Libre el stock de las publicaciones de este SKU, aunque la escritura general siga
                deshabilitada. Ahora mismo se enviaría:
              </p>
            </>
          )}
          {aEnviar.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1" aria-label="Se enviaría">
              {aEnviar.map((f) => (
                <li key={`${f.mlaId}-${f.variantId ?? ''}`}>
                  <strong>{nombrePublicacion(f)}</strong>{f.titulo ? ` · ${f.titulo}` : ''}: {ACCION[f.accion].texto(f).toLowerCase()}
                  {f.accion === 'enviar' && f.stockMl != null ? ` (hoy ${n(f.stockMl)})` : ''}
                </li>
              ))}
            </ul>
          ) : (
            !(depositoEncendido && depositosFull.length > 0) && <p className="text-slate-500">Nada por ahora: Mercado Libre ya tiene el stock calculado.</p>
          )}
          {depositoEncendido && depositosFull.length > 0 && (
            <>
              <p>Stock de depósito de publicaciones Full (el stock de Full no se toca y no se pausan):</p>
              <ul className="list-disc pl-5 space-y-1" aria-label="Stock de depósito que se actualizaría">
                {depositosFull.map((f) => (
                  <li key={`${f.mlaId}-${f.variantId ?? ''}`}>
                    <strong>{nombrePublicacion(f)}</strong>{f.titulo ? ` · ${f.titulo}` : ''}: {n(f.cantidadAEnviar)} (hoy {n(f.stockDeposito)}; en Full {n(f.stockEnFull)}, no se toca)
                  </li>
                ))}
              </ul>
            </>
          )}
          {!depositoEncendido && depositosFull.length > 0 && (
            <p className="text-slate-600">
              {n(depositosFull.length)} {depositosFull.length === 1 ? 'publicación' : 'publicaciones'} Full: su stock de depósito no se escribe
              mientras "{SWITCH_DEPOSITO_FULL}" esté apagado.
            </p>
          )}
          {omitidas.length > 0 && (
            <p className="text-slate-600">
              {n(omitidas.length)} publicaciones no se escriben (ver el detalle en la tabla), por ejemplo combos con otros SKU sin sincronizar.
            </p>
          )}
          <div className="flex justify-end gap-3 pt-3 border-t">
            <Button variant="ghost" onClick={() => setConfirmar(null)} disabled={guardando}>Cancelar</Button>
            <Button onClick={() => void confirmarSincronizacion()} loading={guardando}>
              {confirmar === 'packs' ? 'Confirmar y sincronizar' : 'Confirmar y empezar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Confirm
        open={aDetener !== null}
        title={`Dejar de sincronizar ${aDetener ? etiqueta(aDetener.sku) : ''}`}
        message={
          `Deja de sincronizar ${aDetener ? etiqueta(aDetener.sku) : ''}${aDetener && aDetener.packs.length > 0 ? ` y sus packs: ${aDetener.packs.map(etiqueta).join(', ')}` : ''}. `
          + 'El ERP deja de escribir su stock en Mercado Libre. No se cambia nada en las publicaciones: quedan con el stock y el estado que tienen ahora.'
        }
        confirmLabel="Dejar de sincronizar"
        danger
        onCancel={() => setADetener(null)}
        onConfirm={() => void detener()}
      />
    </>
  );
}
